import { gql, useMutation, useQuery } from '@apollo/client';
import { Alert, Box, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '../components/coreComponents/button';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { useIdentity } from '../identityContext';

// Step 2 page for the two-person delete flow. The email link points here
// (/confirm-deletion/<token>) — IdentityProvider gates the route on a
// valid admin session, so unauthenticated users get bounced to /auth and
// land back here after sign-in.
//
// Renders the request preview + a single Confirm button. The token only
// identifies the request; the destructive job is fired by the resolver
// which re-checks superAdmin on the caller. Status is read from
// pendingAdminActionByToken so we can render a precise message when the
// link is no longer valid (already confirmed, cancelled, or expired).
export function ConfirmDeletionPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const identity = useIdentity();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, loading } = useQuery(query, {
    variables: { token: token ?? '' },
    skip: token == null,
    fetchPolicy: 'cache-and-network'
  });

  const [confirm, { loading: confirming }] = useMutation(confirmMutation);
  const [reject, { loading: rejecting }] = useMutation(rejectMutation);

  if (token == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm deletion" onBack={() => navigate('/')} />
        <Alert severity="error">Missing confirmation token.</Alert>
      </MainWrapper>
    );
  }

  if (loading && data == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm deletion" onBack={() => navigate('/')} />
        <Typography variant="body2">Loading…</Typography>
      </MainWrapper>
    );
  }

  const action = data?.pendingAdminActionByToken ?? null;

  if (action == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm deletion" onBack={() => navigate('/')} />
        <Alert severity="error">This confirmation link is invalid or has been deleted.</Alert>
      </MainWrapper>
    );
  }

  if (action.status === 'confirmed') {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm deletion" onBack={() => navigate('/')} />
        <Alert severity="info">
          This request was already confirmed
          {action.confirmedByEmail != null ? ` by ${action.confirmedByEmail}` : ''}
          {action.confirmedAt != null ? ` on ${new Date(action.confirmedAt).toLocaleString()}` : ''}
          . No further action is needed.
        </Alert>
      </MainWrapper>
    );
  }

  if (action.status === 'cancelled') {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm deletion" onBack={() => navigate('/')} />
        <Alert severity="info">This request was cancelled and is no longer actionable.</Alert>
      </MainWrapper>
    );
  }

  if (action.status === 'expired') {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm deletion" onBack={() => navigate('/')} />
        <Alert severity="warning">
          This confirmation link has expired (expired {new Date(action.expiresAt).toLocaleString()}
          ). Ask <strong>{action.initiatedByEmail}</strong> to re-send the request.
        </Alert>
      </MainWrapper>
    );
  }

  const isSoft = action.kind === 'accountSoftDelete';

  return (
    <MainWrapper>
      <MainWrapperHeader
        title={isSoft ? 'Confirm account deletion' : 'Confirm permanent backup purge'}
        description={
          isSoft
            ? 'Tears down the tenant database; backup remains restorable for 180 days.'
            : 'Permanently removes the S3 backup of an already-deleted account.'
        }
        onBack={() => navigate('/')}
      />

      <Stack spacing={4} sx={{ maxWidth: 720 }}>
        <Box
          sx={{
            p: 3,
            borderRadius: 1,
            border: 1,
            borderColor: 'error.light',
            borderLeftWidth: 4,
            borderLeftColor: 'error.main'
          }}
        >
          <Typography
            variant="labelSmall"
            color="text.secondary"
            sx={{ mb: 2, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            You are about to confirm
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1 }}>
            <Typography variant="labelSmall" color="text.secondary">
              Action
            </Typography>
            <Typography variant="body2">
              {isSoft ? 'Soft delete (S3 backup retained 180 days)' : 'Permanent backup purge'}
            </Typography>

            <Typography variant="labelSmall" color="text.secondary">
              Account
            </Typography>
            <Typography variant="body2">
              {action.accountAlias != null && <strong>{action.accountAlias} </strong>}
              <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                {action.accountId}
              </Box>
            </Typography>

            {action.accountOwner != null && (
              <>
                <Typography variant="labelSmall" color="text.secondary">
                  Owner
                </Typography>
                <Typography variant="body2">{action.accountOwner}</Typography>
              </>
            )}

            <Typography variant="labelSmall" color="text.secondary">
              Initiated by
            </Typography>
            <Typography variant="body2">{action.initiatedByEmail}</Typography>

            <Typography variant="labelSmall" color="text.secondary">
              Requested at
            </Typography>
            <Typography variant="body2">{new Date(action.createdAt).toLocaleString()}</Typography>

            <Typography variant="labelSmall" color="text.secondary">
              Expires
            </Typography>
            <Typography variant="body2">{new Date(action.expiresAt).toLocaleString()}</Typography>
          </Box>
        </Box>

        {errorMessage != null && <Alert severity="error">{errorMessage}</Alert>}

        {!identity.isActionConfirmationRecipient && (
          <Alert severity="info">
            Only administrators listed in <code>ADMIN_ACTION_CONFIRMATION_EMAILS</code> can confirm
            a deletion request. The Confirm button is hidden because your account isn't a recipient
            — you can still cancel or resend the request from the originating page.
          </Alert>
        )}

        <Stack direction="row" spacing={3}>
          {identity.isActionConfirmationRecipient && (
            <>
              <Button
                variant="destructive"
                disabled={confirming || rejecting}
                onClick={async () => {
                  setErrorMessage(null);
                  try {
                    const result = await confirm({ variables: { token } });
                    const jobId = result.data?.accountDeleteConfirm.id;
                    if (jobId != null) navigate(`/jobs/${jobId}`);
                  } catch (err: any) {
                    setErrorMessage(err.message ?? 'Failed to confirm');
                  }
                }}
              >
                {confirming
                  ? 'Confirming…'
                  : isSoft
                    ? 'Confirm deletion'
                    : 'Confirm permanent purge'}
              </Button>
              <Button
                variant="secondary"
                disabled={confirming || rejecting}
                onClick={async () => {
                  setErrorMessage(null);
                  const reason = window.prompt('Reason for rejecting (optional):') ?? undefined;
                  try {
                    await reject({ variables: { id: action.id, reason } });
                    navigate('/pending-actions');
                  } catch (err: any) {
                    setErrorMessage(err.message ?? 'Failed to reject');
                  }
                }}
              >
                {rejecting ? 'Rejecting…' : 'Reject'}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </MainWrapper>
  );
}

const query = gql<PendingAdminActionByTokenGQL>`
  query PendingAdminActionByToken($token: String!) {
    pendingAdminActionByToken(token: $token) {
      id
      kind
      accountId
      accountAlias
      accountOwner
      initiatedByEmail
      createdAt
      expiresAt
      confirmedAt
      confirmedByEmail
      status
    }
  }
`;

const confirmMutation = gql<AccountDeleteConfirmGQL>`
  mutation AccountDeleteConfirm($token: String!) {
    accountDeleteConfirm(token: $token) {
      id
      type
      status
    }
  }
`;

const rejectMutation = gql<PendingActionRejectFromDeletionGQL>`
  mutation PendingActionRejectFromDeletion($id: ID!, $reason: String) {
    pendingAdminActionReject(id: $id, reason: $reason) {
      id
      status
    }
  }
`;
